// ClientAccessTierBadge (CFR-VW-040 / row 21.37) — the visual signal for a module's declared
// `clientAccessTier` (the 5 tiers governing whether/how a client session may interact with a module;
// `@perlab/registry` manifest-schema). Presentational only: the tier comes from the manifest (already
// typed + backend-enforced via app/authz/manifest_tier.py); this component only renders it. The
// tone↔tier mapping is a UX choice (restrictive → neutral/warning, open → success) — cosmetic, easily
// tuned. An unknown/removed tier string renders NOTHING (fail-safe: never throw, never show garbage).
import type { ReactElement } from "react";
import { Badge, type BadgeTone } from "@perlab/ui";
import type { ClientAccessTier } from "@perlab/registry";

/** Human-readable label per tier. Exhaustive over ClientAccessTier — a new tier without an entry is a
 *  compile error here (Record<ClientAccessTier, …>), so the badge can never silently miss a tier. */
export const TIER_LABELS: Record<ClientAccessTier, string> = {
  "kearney-internal-only": "Internal only",
  "client-readonly": "Client · read-only",
  "client-comment": "Client · comment",
  "client-collab": "Client · collaborate",
  "client-approval": "Client · approval",
};

/** Semantic tone per tier: most-restrictive is quiet (neutral), progressively opener tiers warm toward
 *  success. A UX choice, not a security signal (the gate is the backend's). */
const TIER_TONES: Record<ClientAccessTier, BadgeTone> = {
  "kearney-internal-only": "neutral",
  "client-readonly": "warning",
  "client-comment": "accent",
  "client-collab": "success",
  "client-approval": "success",
};

export function ClientAccessTierBadge({
  tier,
}: {
  tier: ClientAccessTier;
}): ReactElement | null {
  const label = TIER_LABELS[tier];
  if (!label) return null; // fail-safe: a stale/removed tier string renders nothing, never throws
  return (
    <Badge
      tone={TIER_TONES[tier]}
      role="status"
      aria-label={`Client access tier: ${label}`}
      data-app="client-access-tier-badge"
      data-tier={tier}
    >
      {label}
    </Badge>
  );
}
